import React, { useCallback, useImperativeHandle, useState } from 'react';
import { Modifier, PillSchedule, Schedule as TSchedule, TimeSlot } from '../../../domain/schedule';
import { Ailment, EffectType, Pill } from '../../../domain/status';
import { useTranslation } from 'react-i18next';
import { useGrid } from './useGrid';
import { GrandmaTickResult } from '../../../domain/grandma';
import { DayTime } from './DayTime';
import { DragOrigin } from '../Pills/Pill';
import clsx from 'clsx';
import { useStyles } from './Schedule.styles';
import { Button } from '../../Common/Button';

export interface IScheduleProps {
    availablePills: Pill[];
    schedule: TSchedule;
    currentTime: number;
    disabled: boolean;
    statusEffects: Record<number, GrandmaTickResult>;
    removePillFromOrigin(pill: Pill, origin: DragOrigin): void;
    onStart: (schedule: PillSchedule) => void;
    started: boolean;
}

enum SelectionState {
    None = 0,
    // Maybe = 1,
    Selected = 2,
}

const stateIsValid = (state: PillSchedule): boolean => {
    return Object.keys(state).length > 0 && Object.values(state).some(ps => ps.length > 0);
};

const renderState = (state: SelectionState): string => {
    switch (state) {
        // case SelectionState.Maybe: return '❓';
        case SelectionState.Selected: return '✅';
        default: return '-';
    }
};

type SelectionTable = Record<TimeSlot['time'], Record<Pill['key'], SelectionState>>;

const createState = (selections: SelectionTable, availablePills: Pill[]): PillSchedule => {
    const result: PillSchedule = {};
    Object.entries(selections).forEach(([ time, selections ]) => {
        const pills: Pill[] = Object.entries(selections).map(([ pillKey, status ]) => {
            if (status === SelectionState.Selected) {
                return availablePills.find(p => p.key === pillKey);
            }
            return undefined;
        }).filter(p => p !== undefined) as Pill[];

        if (pills.length > 0) {
            result[time as unknown as TimeSlot['time']] = pills;
        }
    });
    return result;
};

export interface ISchedule {
    removePillFromSlot(pill: Pill, time: TimeSlot['time']): void;
    reset(): void;
}

export const Schedule = React.forwardRef<ISchedule, IScheduleProps>((props, ref) => {
    const { t } = useTranslation();
    const styles = useStyles();

    const rowCount = props.availablePills.length;
    const timeSlotCount = props.schedule.length;
    const grid = useGrid<HTMLTableElement>(timeSlotCount, rowCount);

    const [ state, setState ] = useState<SelectionTable>({});

    const [ pillSchedule, setPillSchedule ] = useState<PillSchedule>({});
    // const valid = stateIsValid(state);
    const valid = true;

    const updateSlot = useCallback((pill: Pill, timeSlot: Pick<TimeSlot, 'time'>, selectionState: (SelectionState | ((current: SelectionState) => SelectionState))) => {
        setState(currentState => {
            const slotData = currentState[timeSlot.time] ?? {};
            const pillState = slotData[pill.key] ?? SelectionState.None;
            let nextSelectionState: SelectionState;
            if (typeof selectionState === 'function') {
                nextSelectionState = selectionState(pillState);
            } else {
                nextSelectionState = selectionState;
            }
            if (pillState === SelectionState.Selected && nextSelectionState !== SelectionState.Selected) {
                props.removePillFromOrigin(pill, timeSlot.time);
            }
            const nextState = { ...currentState, [timeSlot.time]: { ...slotData, [pill.key]: nextSelectionState } };
            const state = createState(nextState, props.availablePills);
            setPillSchedule(state);
            return nextState;
        });
    }, [ props.availablePills, props.removePillFromOrigin ]);

    const handleClick = (pill: Pill, timeSlot: TimeSlot) => () => {
        updateSlot(pill, timeSlot, current => {
            switch (current) {
                // case SelectionState.Maybe:
                //     return SelectionState.Selected;
                case SelectionState.Selected:
                    return SelectionState.None;
                case SelectionState.None:
                    return SelectionState.Selected;
            }
        });
    };

    const handleDrop = (slot: TimeSlot, pill: Pill, origin: DragOrigin) => {
        updateSlot(pill, slot, SelectionState.Selected);
        if (origin !== slot.time) {
            props.removePillFromOrigin(pill, origin);
        }
    };

    useImperativeHandle(ref, () => {
        return {
            removePillFromSlot(pill: Pill, time: TimeSlot['time']) {
                updateSlot(pill, { time }, SelectionState.None);
            },
            reset() {
                setPillSchedule({});
                setState({});
            },
        };
    }, [ updateSlot ]);

    const hasPills = (time: TimeSlot['time']): boolean =>
        Object.values(state[time] ?? {}).some(s => s !== SelectionState.Selected);

    const timeStyles = [
        styles.morning,
        styles.forenoon,
        styles.noon,
        styles.afternoon,
        styles.evening,
        styles.night,
    ];

    return (
        <div className={styles.root}>
            <div className={styles.pillBox}>
                {props.schedule.map((h, i) => {
                    if (!props.started || props.currentTime > h.time) {
                        return null;
                    }
                    return (
                        <div
                            key={i}
                            className={clsx(styles.lid, timeStyles[i])}
                        ></div>
                    );
                })}
                <table ref={grid.ref} className={styles.table} id="pillbox">
                    <thead>
                        <tr>
                            <td className="sr-only"></td>
                            {props.schedule.map((h, i) => {
                                const ailments: Ailment[] = props.statusEffects[h.time]?.statusEffects
                                    .filter(x => x.type === EffectType.Ailment) as Ailment[] ?? [];
                                const selectedPills = Object.entries(state[h.time] ?? {})
                                    .filter(([ pkey, selectionState ]) => selectionState === SelectionState.Selected)
                                    .map(([ pkey ]) => pkey);
                                return (
                                    <th scope="col" key={h.time} className={styles.col}>
                                        <DayTime
                                            index={i}
                                            isActive={props.currentTime === h.time}
                                            isActivated={props.currentTime >= h.time}
                                            timeSlot={h}
                                            statusEffects={ailments}
                                            disabled={props.disabled}
                                            selectedPills={selectedPills}
                                            onDrop={handleDrop}
                                            availablePills={props.availablePills}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {props.availablePills.map((p, i) => (
                            <tr key={i} className="sr-only">
                                <th scope="row">{t(`effect.${p.key}.name`)}</th>
                                {props.schedule.map((h, j) => {
                                    const index = i * timeSlotCount + j;
                                    const selectionState = state[h.time]?.[p.key] ?? SelectionState.None;
                                    return (
                                        <td key={h.time}>
                                            <button
                                                onKeyDown={grid.onKeyDown}
                                                tabIndex={index === grid.selectedIndex ? 0 : -1}
                                                disabled={props.disabled || h.modifiers.includes(Modifier.isGone)}
                                                onClick={handleClick(p, h)}
                                            >
                                                {renderState(selectionState)}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>


            <div className={styles.startWrapper}>
                <Button
                    onClick={() => props.onStart(pillSchedule)}
                    disabled={props.disabled || !valid}
                    id="starttrigger"
                    className={styles.startTrigger}
                >
                    {t('game.planner.start')}
                </Button>
            </div>
        </div>
    );
});
Schedule.displayName = 'Schedule';
